import { useEffect, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useCognitoAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import type { BeangoError } from '@/lib/beango-error';
import { ErrorCode, userMessage } from '@/lib/error-codes';

interface ReconciliationDisplay {
  id: number;
  bank_account_id: number;
  statement_date: string;
  ending_balance: number;
  book_balance: number;
  difference: number;
  status: string;
}

export function Reconciliation() {
  const [unmatchedData, setUnmatchedData] = useState<{
    bank_records: { id: number; transaction_date: string; description: string; amount: number }[];
    total_bank_records: number;
  } | null>(null);
  const [sessions, setSessions] = useState<ReconciliationDisplay[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { idToken } = useCognitoAuth();

  useEffect(() => {
    if (!idToken) return;
    async function fetchData() {
      try {
        const [unmatched, bankAccounts] = await Promise.all([
          api.get<{ bank_records: { id: number; transaction_date: string; description: string; amount: number }[]; total_bank_records: number }>('/api/reconciliation/unmatched', { errorCode: ErrorCode.FETCH_RECONCILIATION, token: idToken! }),
          api.get<{ id: number; current_balance: number }[]>('/api/entities/bank-accounts', { errorCode: ErrorCode.FETCH_RECONCILIATION, token: idToken! }),
        ]);
        setUnmatchedData(unmatched as typeof unmatchedData);
        const mockSessions: ReconciliationDisplay[] = bankAccounts.map((ba, idx) => ({
          id: idx + 1,
          bank_account_id: ba.id,
          statement_date: new Date().toISOString().split('T')[0],
          ending_balance: Number(ba.current_balance),
          book_balance: Number(ba.current_balance),
          difference: 0,
          status: idx === 0 ? 'completed' : 'pending',
        }));
        setSessions(mockSessions);
      } catch (err) {
        setError(err instanceof Error && 'code' in err ? userMessage((err as BeangoError).code) : 'Failed to fetch reconciliation data');
      } finally {
        setLoading(false);
      }
    }
    void fetchData();
  }, [idToken]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
    }).format(Math.abs(amount));
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-CA');
  };

  const getStatusVariant = (status: string): 'secondary' | 'outline' | 'default' => {
    switch (status) {
      case 'completed': return 'secondary';
      case 'in_progress': return 'outline';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="animate-spin text-3xl mb-4"><i className="bi bi-arrow-repeat"></i></div>
        <p className="text-muted-foreground">Loading reconciliation...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <i className="bi bi-exclamation-triangle text-3xl text-red-600 mb-4"></i>
        <p className="text-muted-foreground">Error: {error}</p>
      </div>
    );
  }

  const unreconciledItems = unmatchedData?.bank_records ?? [];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 pb-4 border-b border-border">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Bank Reconciliation</h1>
          <p className="text-sm text-muted-foreground">Match bank statements to book records</p>
        </div>
        <Button variant="default" size="sm">
          <i className="bi bi-plus"></i> New Reconciliation
        </Button>
      </div>

      {unreconciledItems.length > 0 && (
        <Card className="border-[var(--accent)] bg-[var(--accent-bg)]">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <i className="bi bi-exclamation-triangle text-[var(--accent)] text-xl mt-0.5"></i>
              <div className="flex-1">
                <p className="font-semibold text-foreground">{unreconciledItems.length} unreconciled transactions</p>
                <p className="text-sm text-muted-foreground">These transactions need to be matched to your bank statement.</p>
              </div>
              <Button variant="outline" size="sm" className="border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent-bg)]">
                Review Now
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Active Reconciliation</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="flex flex-col gap-1">
              <span className="text-sm text-muted-foreground">Bank Statement Balance</span>
              <span className="text-xl font-mono font-semibold">{formatCurrency(Number(sessions[0]?.ending_balance ?? 0))}</span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-sm text-muted-foreground">Book Balance</span>
              <span className="text-xl font-mono font-semibold">{formatCurrency(Number(sessions[0]?.book_balance ?? 0))}</span>
            </div>
            <div className="flex flex-col gap-1 p-3 bg-[var(--accent-bg)] rounded-lg border border-[var(--accent)]">
              <span className="text-sm text-[var(--accent)]">Difference</span>
              <span className="text-xl font-mono font-semibold text-foreground">{formatCurrency(Number(sessions[0]?.difference ?? 0))}</span>
            </div>
          </div>
          
          <div className="space-y-6">
            <div>
              <h4 className="text-sm font-semibold mb-3">Deposits in Transit</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="py-2 px-3 text-left font-medium text-muted-foreground">Date</th>
                      <th className="py-2 px-3 text-left font-medium text-muted-foreground">Description</th>
                      <th className="py-2 px-3 text-right font-medium text-muted-foreground">Amount</th>
                      <th className="py-2 px-3 text-center font-medium text-muted-foreground">
                        <input type="checkbox" />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {unreconciledItems.filter(i => Number(i.amount) > 0).map((item) => (
                      <tr key={item.id} className="border-b border-border hover:bg-muted/50">
                        <td className="py-2 px-3 font-mono">{formatDate(item.transaction_date)}</td>
                        <td className="py-2 px-3">{item.description}</td>
                        <td className="py-2 px-3 text-right font-mono text-green-600">{formatCurrency(Number(item.amount))}</td>
                        <td className="py-2 px-3 text-center"><input type="checkbox" /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-semibold mb-3">Outstanding Checks</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="py-2 px-3 text-left font-medium text-muted-foreground">Date</th>
                      <th className="py-2 px-3 text-left font-medium text-muted-foreground">Description</th>
                      <th className="py-2 px-3 text-right font-medium text-muted-foreground">Amount</th>
                      <th className="py-2 px-3 text-center font-medium text-muted-foreground">
                        <input type="checkbox" />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {unreconciledItems.filter(i => Number(i.amount) < 0).map((item) => (
                      <tr key={item.id} className="border-b border-border hover:bg-muted/50">
                        <td className="py-2 px-3 font-mono">{formatDate(item.transaction_date)}</td>
                        <td className="py-2 px-3">{item.description}</td>
                        <td className="py-2 px-3 text-right font-mono text-red-600">-{formatCurrency(Math.abs(Number(item.amount)))}</td>
                        <td className="py-2 px-3 text-center"><input type="checkbox" /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <Button variant="outline">Save Progress</Button>
            <Button variant="default">Complete Reconciliation</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recent Reconciliations</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="py-3 px-4 text-left font-medium text-muted-foreground">Bank Account</th>
                  <th className="py-3 px-4 text-left font-medium text-muted-foreground">Statement Date</th>
                  <th className="py-3 px-4 text-right font-medium text-muted-foreground">Ending Balance</th>
                  <th className="py-3 px-4 text-right font-medium text-muted-foreground">Book Balance</th>
                  <th className="py-3 px-4 text-right font-medium text-muted-foreground">Difference</th>
                  <th className="py-3 px-4 text-left font-medium text-muted-foreground">Status</th>
                  <th className="py-3 px-4 text-left font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((stmt) => (
                  <tr key={stmt.id} className="border-b border-border hover:bg-muted/50">
                    <td className="py-3 px-4">Bank Account #{stmt.bank_account_id}</td>
                    <td className="py-3 px-4 font-mono">{formatDate(stmt.statement_date)}</td>
                    <td className="py-3 px-4 text-right font-mono">{formatCurrency(stmt.ending_balance)}</td>
                    <td className="py-3 px-4 text-right font-mono">{formatCurrency(stmt.book_balance)}</td>
                    <td className={`py-3 px-4 text-right font-mono ${stmt.difference === 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {stmt.difference === 0 ? '—' : formatCurrency(stmt.difference)}
                    </td>
                    <td className="py-3 px-4">
                      <Badge variant={getStatusVariant(stmt.status)}>
                        {stmt.status.replace('_', ' ')}
                      </Badge>
                    </td>
                    <td className="py-3 px-4">
                      <Button variant="ghost" size="sm">
                        <i className="bi bi-eye"></i>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
